import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete, handleDeleteError } from '../utils/deleteHelper';

const EMPTY = {
  name: '', code: '', short_name: '', address: '', phone: '', email: '', ntn: '',
  status: 'active', logo_url: '',
  color_primary:   '#1d4ed8',
  color_secondary: '#1e3a8a',
  color_accent:    '#60a5fa',
  sidebar_color:   '#1d4ed8',
  header_color:    '#2563eb',
  icon_color:      '#93c5fd',
};

// Preset brand palettes — covers both 3-color and full 6-color configs
const PALETTES = [
  { label: 'Blue',   primary: '#1d4ed8', secondary: '#1e3a8a', accent: '#60a5fa', sidebar: '#1d4ed8', header: '#2563eb', icon: '#93c5fd' },
  { label: 'Green',  primary: '#166534', secondary: '#14532d', accent: '#4ade80', sidebar: '#166534', header: '#15803d', icon: '#86efac' },
  { label: 'Purple', primary: '#6b21a8', secondary: '#581c87', accent: '#a855f7', sidebar: '#6b21a8', header: '#7c3aed', icon: '#d8b4fe' },
  { label: 'Red',    primary: '#9f1239', secondary: '#881337', accent: '#f43f5e', sidebar: '#9f1239', header: '#be123c', icon: '#fda4af' },
  { label: 'Teal',   primary: '#0f766e', secondary: '#134e4a', accent: '#2dd4bf', sidebar: '#0f766e', header: '#0d9488', icon: '#99f6e4' },
  { label: 'Slate',  primary: '#334155', secondary: '#1e293b', accent: '#94a3b8', sidebar: '#334155', header: '#475569', icon: '#cbd5e1' },
];

function getInitials(name, shortName) {
  if (shortName) return shortName;
  if (!name) return '??';
  return name.split(' ').filter(w => w.length > 1).map(w => w[0]).join('').slice(0, 3).toUpperCase() || name.slice(0, 2).toUpperCase();
}

export default function Companies() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const res = await api.get('/api/companies'); setCompanies(res.data); }
    catch (err) { console.error('companies:', err); setCompanies([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user?.role]);

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.code || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.ntn  || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/api/companies/${editing}`, form);
      else         await api.post('/api/companies', form);
      setShowForm(false); setForm(EMPTY); setEditing(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleEdit = (c) => {
    setForm({
      name:      c.name,
      code:      c.code        || '',
      short_name: c.short_name || '',
      address:   c.address     || '',
      phone:     c.phone       || '',
      email:     c.email       || '',
      ntn:       c.ntn         || '',
      status:    c.status      || 'active',
      logo_url:  c.logo_url    || '',
      color_primary:   c.color_primary   || '#1d4ed8',
      color_secondary: c.color_secondary || '#1e3a8a',
      color_accent:    c.color_accent    || '#60a5fa',
      sidebar_color:   c.sidebar_color   || c.color_primary   || '#1d4ed8',
      header_color:    c.header_color    || c.color_primary   || '#2563eb',
      icon_color:      c.icon_color      || c.color_accent    || '#93c5fd',
    });
    setEditing(c.id); setShowForm(true);
  };

  const applyPalette = (p) => {
    setForm(f => ({
      ...f,
      color_primary:   p.primary,
      color_secondary: p.secondary,
      color_accent:    p.accent,
      sidebar_color:   p.sidebar,
      header_color:    p.header,
      icon_color:      p.icon,
    }));
  };

  const handleDeactivate = async (id, name) => {
    if (!confirmDelete('company', name)) return;
    try { await api.delete(`/api/companies/${id}`); load(); }
    catch (err) { handleDeleteError(err); }
  };

  if (user && user.role !== 'admin') return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center bg-yellow-50 border border-yellow-200 rounded-xl p-8">
        <div className="text-4xl mb-3">⚠️</div>
        <h2 className="text-xl font-bold text-yellow-800">Admin Access Required</h2>
        <p className="text-yellow-600 mt-2">Only administrators can manage companies.</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Company Management</h1>
          <p className="text-gray-500 text-sm">{companies.length} companies registered</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
          className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
          ➕ Add Company
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, code, NTN..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{editing ? 'Edit Company' : 'Add Company'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">

              {/* ── Basic Info ── */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Company Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                    <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. Shahzad Textile Mills" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Code *</label>
                    <input type="text" value={form.code} onChange={e => setForm({...form, code: e.target.value})} required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. STM" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Short Name <span className="text-gray-400 font-normal">(sidebar badge)</span></label>
                    <input type="text" value={form.short_name} onChange={e => setForm({...form, short_name: e.target.value})} maxLength={10}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. STM or SC" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">NTN</label>
                    <input type="text" value={form.ntn} onChange={e => setForm({...form, ntn: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <textarea value={form.address} onChange={e => setForm({...form, address: e.target.value})} rows={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Logo ── */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Logo</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL <span className="text-gray-400 font-normal">(optional)</span></label>
                    <input type="url" value={form.logo_url} onChange={e => setForm({...form, logo_url: e.target.value})}
                      placeholder="https://example.com/logo.png"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <p className="text-xs text-gray-400 mt-1">Paste an image URL. If blank, initials badge is shown instead.</p>
                  </div>
                  {/* Logo preview */}
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl border-2 border-gray-200 flex items-center justify-center overflow-hidden bg-gray-50">
                      {form.logo_url
                        ? <img src={form.logo_url} alt="logo preview" className="w-12 h-12 object-contain"
                            onError={e => { e.target.style.display='none'; }} />
                        : <span className="text-gray-500 font-bold text-sm">
                            {getInitials(form.name, form.short_name)}
                          </span>
                      }
                    </div>
                    <div className="text-xs text-gray-500">
                      <p className="font-medium">{form.name || 'Company Name'}</p>
                      <p className="text-gray-400">{getInitials(form.name, form.short_name)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Brand Colors ── */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">🎨 Brand Colors</p>
                    <p className="text-xs text-gray-500">Applied to sidebar, header, and UI for this company's users</p>
                  </div>
                  {/* Live Sidebar Preview */}
                  <div className="flex-shrink-0">
                    <div className="rounded-lg overflow-hidden shadow-md w-14 h-20 flex flex-col"
                      style={{ background: `linear-gradient(to bottom, ${form.sidebar_color || form.color_primary}, ${form.color_secondary})` }}>
                      <div className="px-1.5 pt-1.5 pb-1 border-b border-white/20">
                        <div className="w-full h-2 rounded bg-white/30" />
                      </div>
                      <div className="flex-1 flex flex-col justify-center gap-1 px-1.5">
                        <div className="w-full h-1.5 rounded bg-white/40" />
                        <div className="w-3/4 h-1.5 rounded bg-white/25" />
                        <div className="w-full h-1.5 rounded bg-white/25" />
                      </div>
                      <div className="px-1.5 pb-1.5 flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full" style={{ background: form.icon_color || form.color_accent }} />
                        <div className="w-5 h-1 rounded bg-white/30" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 text-center mt-1">Preview</p>
                  </div>
                </div>

                {/* Quick Palette Presets */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Quick Presets:</p>
                  <div className="flex flex-wrap gap-2">
                    {PALETTES.map(p => (
                      <button key={p.label} type="button" onClick={() => applyPalette(p)}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-gray-200 hover:border-gray-400 text-xs text-gray-600 transition-colors">
                        <span className="w-4 h-4 rounded-full inline-block border border-white shadow-sm"
                          style={{ background: `linear-gradient(135deg, ${p.primary}, ${p.secondary})` }} />
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color Pickers — Row 1: Primary Colors */}
                <div>
                  <p className="text-xs text-gray-500 mb-2 font-medium">Gradient Colors</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      { key: 'color_primary',   label: 'Primary (top)' },
                      { key: 'color_secondary', label: 'Secondary (bottom)' },
                      { key: 'color_accent',    label: 'Accent' },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                        <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-2 py-1.5">
                          <input type="color" value={form[key]}
                            onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                            className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent p-0" />
                          <span className="text-xs font-mono text-gray-500">{form[key]}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Color Pickers — Row 2: UI-specific */}
                <div>
                  <p className="text-xs text-gray-500 mb-2 font-medium">UI Element Colors</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      { key: 'sidebar_color', label: 'Sidebar Top' },
                      { key: 'header_color',  label: 'Header Accent' },
                      { key: 'icon_color',    label: 'Icons / Dots' },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                        <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-2 py-1.5">
                          <input type="color" value={form[key]}
                            onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                            className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent p-0" />
                          <span className="text-xs font-mono text-gray-500">{form[key]}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update Company' : 'Add Company'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Company Cards */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl animate-pulse mb-2">🏢</div>
          <p>Loading companies...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-5xl mb-3">🏢</div>
          <p>No companies found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(c => {
            const primary   = c.color_primary   || '#1d4ed8';
            const secondary = c.color_secondary || '#1e3a8a';
            const accent    = c.color_accent    || '#60a5fa';
            const sidebar   = c.sidebar_color   || primary;
            const initials  = getInitials(c.name, c.short_name);

            return (
              <div key={c.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                {/* Gradient color banner */}
                <div className="h-2" style={{ background: `linear-gradient(to right, ${sidebar}, ${secondary})` }} />

                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {/* Logo or initials badge */}
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold shadow-sm overflow-hidden flex-shrink-0"
                        style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}>
                        {c.logo_url
                          ? <img src={c.logo_url} alt={initials} className="w-10 h-10 object-contain"
                              onError={e => { e.target.style.display='none'; }} />
                          : <span className="text-xs font-extrabold">{initials}</span>
                        }
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{c.name}</h3>
                        <p className="text-xs text-gray-500 font-mono">{c.code}{c.short_name ? ` · ${c.short_name}` : ''}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                      c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {c.status}
                    </span>
                  </div>

                  <div className="space-y-1.5 text-sm text-gray-600">
                    {c.ntn     && <div className="flex items-center gap-2"><span>🔢</span><span>NTN: {c.ntn}</span></div>}
                    {c.phone   && <div className="flex items-center gap-2"><span>📞</span><span>{c.phone}</span></div>}
                    {c.email   && <div className="flex items-center gap-2"><span>✉️</span><span className="truncate">{c.email}</span></div>}
                    {c.address && <div className="flex items-center gap-2"><span>📍</span><span className="truncate">{c.address}</span></div>}
                  </div>

                  {/* Brand color swatches */}
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-400">Brand:</span>
                    <div className="flex gap-1">
                      {[primary, secondary, accent, c.sidebar_color, c.header_color, c.icon_color]
                        .filter(Boolean)
                        .filter((v, i, a) => a.indexOf(v) === i) // unique
                        .slice(0, 6)
                        .map((color, i) => (
                          <div key={i} title={color}
                            className="w-4 h-4 rounded-full border border-white shadow-sm"
                            style={{ background: color }} />
                        ))
                      }
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2 pt-3 border-t border-gray-50">
                    <button onClick={() => handleEdit(c)}
                      className="flex-1 text-center text-sm py-1.5 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-colors">
                      ✏️ Edit
                    </button>
                    <button onClick={() => handleDeactivate(c.id, c.name)}
                      className="flex-1 text-center text-sm py-1.5 border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors">
                      🚫 Deactivate
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
